import React, { useEffect, useRef, useState, useCallback } from "react";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import { api } from "../lib/api";
import { ChatCircleDots, PaperPlaneTilt, Sparkle, Briefcase, X, ClockCounterClockwise, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtAgo = (iso) => {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

export default function Chatbot() {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [applications, setApplications] = useState([]);
    const [history, setHistory] = useState([]);
    const [jobId, setJobId] = useState("");
    const [jobMeta, setJobMeta] = useState(null);
    const feedRef = useRef(null);

    useEffect(() => {
        feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight, behavior: "smooth" });
    }, [messages]);

    const loadHistory = useCallback(async () => {
        try {
            const { data } = await api.get("/chat/sessions");
            setHistory(data);
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        api.get("/applications/mine").then(({ data }) => setApplications(data)).catch(() => {});
        loadHistory();
    }, [loadHistory]);

    const send = async (text) => {
        if (!text.trim()) return;
        const userMsg = { role: "user", content: text };
        setMessages((m) => [...m, userMsg]);
        setInput("");
        setLoading(true);
        try {
            const payload = { message: text, session_id: sessionId };
            if (jobId) payload.job_id = jobId;
            const { data } = await api.post("/chat/message", payload);
            setSessionId(data.session_id);
            setMessages(data.messages);
            if (data.job_id) {
                setJobMeta({ job_id: data.job_id, title: data.job_title, company: data.job_company });
            }
            loadHistory();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "AI unavailable");
        } finally {
            setLoading(false);
        }
    };

    const startNewWithJob = (j) => {
        setMessages([]);
        setSessionId(null);
        setJobId(j.job_id);
        setJobMeta({ job_id: j.job_id, title: j.job_title, company: j.company_name });
    };

    const clearJob = () => {
        setMessages([]);
        setSessionId(null);
        setJobId("");
        setJobMeta(null);
    };

    const newConversation = () => {
        setMessages([]);
        setSessionId(null);
        setJobId("");
        setJobMeta(null);
    };

    const resumeSession = (s) => {
        setSessionId(s.session_id);
        setMessages(s.messages || []);
        setJobId(s.job_id || "");
        setJobMeta(
            s.job_id
                ? { job_id: s.job_id, title: s.job_title, company: s.job_company }
                : null
        );
    };

    const deleteSession = async (s, ev) => {
        ev.stopPropagation();
        if (!window.confirm("Delete this conversation?")) return;
        try {
            await api.delete(`/chat/sessions/${s.session_id}`);
            if (s.session_id === sessionId) newConversation();
            loadHistory();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete");
        }
    };

    return (
        <div data-testid="chatbot-page">
            <Navbar />
            <section className="max-w-[1200px] mx-auto px-6 py-8 grid md:grid-cols-[260px_1fr] gap-6">
                {/* History sidebar */}
                <aside className="md:sticky md:top-20 self-start" data-testid="chat-history-sidebar">
                    <div className="flex items-center justify-between mb-3">
                        <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-slate-500 inline-flex items-center gap-1.5">
                            <ClockCounterClockwise size={12} /> History
                        </div>
                        <button
                            data-testid="chat-new-btn"
                            onClick={newConversation}
                            title="New conversation"
                            className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-slate-300 hover:bg-slate-950 hover:text-white"
                        >
                            <Plus size={12} weight="bold" /> New
                        </button>
                    </div>
                    {history.length === 0 ? (
                        <div className="text-xs text-slate-500 border border-dashed border-slate-300 p-3">
                            Your past conversations will appear here.
                        </div>
                    ) : (
                        <div className="space-y-1.5 max-h-[60vh] overflow-auto pr-1">
                            {history.map((s) => (
                                <div
                                    key={s.session_id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => resumeSession(s)}
                                    onKeyDown={(e) => e.key === "Enter" && resumeSession(s)}
                                    data-testid={`chat-history-row-${s.session_id}`}
                                    className={`group cursor-pointer text-left text-xs p-2 border ${
                                        sessionId === s.session_id
                                            ? "bg-slate-950 text-white border-slate-950"
                                            : "border-slate-200 hover:border-slate-950"
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span
                                            className={`font-mono-plex uppercase tracking-widest text-[10px] truncate ${
                                                sessionId === s.session_id ? "text-blue-200" : "text-blue-600"
                                            }`}
                                        >
                                            {s.job_title || "Generic skill coach"}
                                        </span>
                                        <button
                                            data-testid={`chat-delete-${s.session_id}`}
                                            onClick={(e) => deleteSession(s, e)}
                                            className={`opacity-0 group-hover:opacity-100 ${
                                                sessionId === s.session_id ? "text-blue-200" : "text-slate-400 hover:text-red-600"
                                            }`}
                                            title="Delete"
                                        >
                                            <Trash size={12} />
                                        </button>
                                    </div>
                                    <div className="mt-1 line-clamp-2 leading-snug">
                                        {s.preview || `${s.message_count} messages`}
                                    </div>
                                    <div className={`mt-1 text-[10px] ${sessionId === s.session_id ? "text-slate-400" : "text-slate-400"}`}>
                                        {fmtAgo(s.updated_at)} · {s.message_count} msgs
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </aside>

                {/* Main panel */}
                <div>
                <div className="flex items-center gap-2 font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-2">
                    <Sparkle size={14} weight="fill" /> Automated screening
                </div>
                <h1 className="font-display text-4xl">
                    {jobMeta ? "Role-specific screening." : "Show the model what you know."}
                </h1>
                <p className="text-slate-600 mt-2">
                    {jobMeta
                        ? `Tailored questions for ${jobMeta.title} @ ${jobMeta.company}. ~5–6 exchanges.`
                        : "Conversational skill assessment. Under 10 minutes. No trick questions."}
                </p>

                {jobMeta && (
                    <div
                        data-testid="chatbot-job-badge"
                        className="mt-4 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs"
                    >
                        <Briefcase size={14} className="text-blue-600" />
                        <span className="font-mono-plex uppercase tracking-widest text-blue-700">
                            Screening for: {jobMeta.title}
                        </span>
                        <button
                            data-testid="chatbot-clear-job"
                            onClick={clearJob}
                            className="ml-1 text-blue-700 hover:text-slate-950"
                            title="Switch back to generic skill coach"
                        >
                            <X size={14} />
                        </button>
                    </div>
                )}

                {!jobMeta && applications.length > 0 && messages.length === 0 && (
                    <div className="mt-4 border border-slate-200 p-4 bg-slate-50" data-testid="chatbot-job-picker">
                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">
                            Optional · Role-specific screening
                        </div>
                        <p className="text-sm text-slate-700 mb-3">
                            Pick a role you applied to — the screener will tailor every question to its requirements.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {applications.slice(0, 6).map((a) => (
                                <button
                                    key={a.application_id}
                                    data-testid={`chatbot-pick-job-${a.job_id}`}
                                    onClick={() => startNewWithJob(a)}
                                    className="text-xs px-3 py-1.5 border border-slate-300 hover:bg-slate-950 hover:text-white"
                                >
                                    {a.job_title}{" "}
                                    <span className="text-slate-400">· {a.company_name}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-6 border border-slate-200 flex flex-col h-[70vh]">
                    <div ref={feedRef} className="flex-1 overflow-auto p-6 space-y-4" data-testid="chat-feed">
                        {messages.length === 0 && (
                            <div className="text-center text-sm text-slate-500 py-16">
                                <ChatCircleDots size={40} weight="duotone" className="mx-auto text-blue-600" />
                                <p className="mt-3">Introduce yourself: what do you do, and what's your favourite tool in your stack?</p>
                                <div className="mt-4 flex flex-wrap gap-2 justify-center">
                                    {[
                                        "I'm a frontend engineer focused on React and design systems.",
                                        "I'm a data scientist, mostly Python/pandas/Snowflake.",
                                        "I'm a product designer moving into product management.",
                                    ].map((s) => (
                                        <button
                                            key={s}
                                            data-testid={`chat-starter-${s.slice(0, 12)}`}
                                            onClick={() => send(s)}
                                            className="text-xs px-3 py-1.5 border border-slate-300 hover:bg-slate-950 hover:text-white"
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div
                                key={m.ts ? `${m.ts}-${m.role}` : `m-${i}`}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                data-testid={`chat-msg-${m.role}-${i}`}
                            >
                                <div
                                    className={`max-w-[80%] px-4 py-3 text-sm ${
                                        m.role === "user"
                                            ? "bg-slate-950 text-white"
                                            : "bg-slate-100 text-slate-900 border border-slate-200"
                                    }`}
                                >
                                    <div className="font-mono-plex text-[10px] uppercase tracking-widest opacity-60 mb-1">
                                        {m.role === "user" ? "you" : "coach"}
                                    </div>
                                    <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                                </div>
                            </div>
                        ))}
                        {loading && (
                            <div className="text-xs font-mono-plex uppercase tracking-widest text-slate-400">
                                coach is typing…
                            </div>
                        )}
                    </div>

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            send(input);
                        }}
                        className="border-t border-slate-200 p-4 flex gap-2"
                        data-testid="chat-form"
                    >
                        <input
                            data-testid="chat-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Type your answer…"
                            className="flex-1 px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                        />
                        <button
                            data-testid="chat-send-btn"
                            disabled={loading}
                            className="px-4 bg-blue-600 text-white hover:bg-slate-950 disabled:opacity-60 inline-flex items-center gap-1.5"
                        >
                            <PaperPlaneTilt size={16} /> Send
                        </button>
                    </form>
                </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
